import {
  generateUUID,
  getRetryDelay,
  log,
  mergeObjectDeep,
  toObjectString,
  warnLog
} from "@zyzgroup/core-common";

type SocketMessage = string | Blob | ArrayBuffer;

type WebSocketClientRequest = {
  requestid: number;
  resolve?: (value: any) => void;
};

type SocketCreator = (
  url: string,
  protocols?: string | string[],
  options?: Record<string, any>
) => WebSocket;

export function createWebSocket(
  url: string,
  protocols?: string | string[],
  options?: Record<string, any>
): WebSocket {
  return new WebSocket(url);
}

// export function createSocketJS(
//   url: string,
//   protocols?: string | string[],
//   options?: Record<string, any>
// ): WebSocket {
//   return new SockJS(url, null, options);
// }

// export function createWxWebSocket(
//   url: string,
//   protocols?: string | string[],
//   options?: Record<string, any>
// ): WebSocket {
//   return wx.connectSocket(
//     mergeObjectDeep(
//       {
//         url,
//         ...options
//       },
//       {
//         header: {
//           // "Cookie": "JSESSIONID=dummy",
//           // 'content-type': 'application/json'
//         },
//         timeout: 120000
//         // success: console.log,
//         // fail:console.log
//       }
//     )
//   );
// }

// export function createAliWebSocket(
//   url: string,
//   protocols?: string | string[],
//   options?: Record<string, any>
// ): WebSocket {
//   return my.connectSocket(
//     mergeObjectDeep(
//       {
//         url,
//         ...options
//       },
//       {
//         header: {
//           // "Cookie": "JSESSIONID=dummy",
//           // 'content-type': 'application/json'
//         },
//         timeout: 120000,
//         multiple: true
//         // success: console.log,
//         // fail:console.log
//       }
//     )
//   );
// }

export function getVertxRegisterBaseUrl(baseUrl: string) {
  return `${baseUrl}eventbus/${generateUUID(3, "number")}/${generateUUID(
    8
  )}/websocket`;
}

export class WebSocketClient {
  static CONNECTING = WebSocket.CONNECTING; // 0
  static OPEN = WebSocket.OPEN; // 1
  static CLOSING = WebSocket.CLOSING; // 2
  static CLOSED = WebSocket.CLOSED; // 3

  url: string;
  socketCreatorProtocols?: string | string[];
  socketCreatorOptions: Record<string, any>;
  socketCreator: SocketCreator;

  socket?: WebSocket;
  state:
    | typeof WebSocketClient.CONNECTING
    | typeof WebSocketClient.OPEN
    | typeof WebSocketClient.CLOSING
    | typeof WebSocketClient.CLOSED = WebSocketClient.CONNECTING;

  messageQueue: Record<string, any>[] = [];

  pingInterval: number;
  pingTimerID?: ReturnType<typeof setInterval>;

  reconnect: boolean;
  reconnectAttempts = 0;
  maxReconnectAttempts: number;
  reconnectTimerID?: ReturnType<typeof setTimeout>;

  onopen?: () => void;
  onclose?: (e: CloseEvent) => void;
  onerror?: (e: any) => void;

  requestid = 0;
  waitings: Record<number, WebSocketClientRequest> = {};
  listeners: Record<string, ((err: any, message?: any) => void)[]> = {};

  // wss://echo.websocket.org/
  // wss://xinge.ruolin-link.com/
  // wss://test.xinge.me/
  constructor(
    url = "",
    options?: {
      socketCreatorProtocols?: string | string[];
      socketCreatorOptions?: Record<string, any>;
      socketCreator?: SocketCreator;
      pingInterval?: number;
      onopen?: () => void;
      onclose?: (e: CloseEvent) => void;
      onerror?: (e: Event) => void;
      reconnect?: boolean;
      maxReconnectAttempts?: number;
    }
  ) {
    this.url = url;
    this.socketCreatorProtocols = options?.socketCreatorProtocols;
    this.socketCreatorOptions = options?.socketCreatorOptions || {};
    this.socketCreator = options?.socketCreator || createWebSocket;
    this.pingInterval = options?.pingInterval || 5000;
    this.onopen = options?.onopen;
    this.onclose = options?.onclose;
    this.onerror = options?.onerror;
    this.reconnect =
      typeof options?.reconnect == "boolean" ? options.reconnect : true;
    this.maxReconnectAttempts =
      typeof options?.maxReconnectAttempts == "number"
        ? options.maxReconnectAttempts
        : Infinity;
    this._connect();
  }

  public open() {
    this.reconnectAttempts = 0;
    this._connect();
  }

  public close() {
    this.state = WebSocketClient.CLOSING;
    this.socket?.close();
  }

  private _connect() {
    if (
      this.socket?.readyState === WebSocketClient.CONNECTING ||
      this.socket?.readyState === WebSocketClient.OPEN
    ) {
      return;
    }
    this.state = WebSocketClient.CONNECTING;
    this.socket = this.socketCreator(
      this.url,
      this.socketCreatorProtocols,
      this.socketCreatorOptions
    );

    this.socket.onopen = () => {
      log(`(${this.url}) onopen`);
      this.state = WebSocketClient.OPEN;
      // clear reconnect
      if (this.reconnectTimerID) {
        clearTimeout(this.reconnectTimerID);
        this.reconnectTimerID = undefined;
      }
      // 继续发送未发消息
      const copies = this.messageQueue.slice();
      this.messageQueue = [];
      copies.forEach(this._send.bind(this));
      // Send the first ping then send a ping every pingInterval milliseconds
      this.ping();
      this.pingTimerID = setInterval(this.ping.bind(this), this.pingInterval);
      this.onopen?.();
    };

    this.socket.onclose = (e: CloseEvent) => {
      const { code, reason, wasClean } = e;
      log(
        `(${this.url}) onclose: code(${code}) reason(${reason}) wasClean(${wasClean})`
      );
      this.state = WebSocketClient.CLOSED;
      this.socket = undefined;
      // clear ping
      if (this.pingTimerID) {
        clearInterval(this.pingTimerID);
        this.pingTimerID = undefined;
      }
      this.onclose?.(e);
      // reconnect
      if (
        this.reconnect &&
        this.reconnectAttempts < this.maxReconnectAttempts
      ) {
        this.reconnectTimerID = setTimeout(
          this._connect.bind(this),
          getRetryDelay(this.reconnectAttempts++)
        );
      }
    };

    this.socket.onerror = (e: Event) => {
      log(`(${this.url}) onerror: ${e}`);
      this.onerror?.(e);
    };

    // 指定收到数据类型
    // this.socket.binaryType = "blob";
    // this.socket.binaryType = "arraybuffer";

    this.socket.onmessage = (e: MessageEvent) => {
      log(`(${this.url}) onmessage: { type: ${e.type}, data: ${e.data} }`);
      if (typeof e.data === "string") {
        // 可能需要根据协议变更传输格式
        let jsons: string[];
        const originData = e.data.substring(1);
        try {
          jsons = JSON.parse(originData);
        } catch (ex: any) {
          jsons = [
            JSON.stringify({
              type: "err",
              failureCode: "fe-json-parse-error",
              failureType: ex.toString(),
              message: originData
            })
          ];
        }
        jsons.forEach((j) => {
          const json = JSON.parse(j);
          if (
            json.body &&
            Object.prototype.hasOwnProperty.call(json.body, "requestid")
          ) {
            // 回应 send
            const request = this.waitings[json.body.requestid];
            if (request) {
              request.resolve?.(json.body);
            } else {
              warnLog(
                `(${this.url}) onmessage: found no associated request, already timed out`
              );
            }
          } else if (this.listeners[json.address]) {
            // 回应注册监听回调
            const listeners = this.listeners[json.address];
            for (let i = 0; i < listeners.length; i++) {
              if (json.type === "err") {
                listeners[i]({
                  failureCode: json.failureCode,
                  failureType: json.failureType,
                  message: json.message
                });
              } else {
                listeners[i](null, json.body);
              }
            }
          } else {
            // unhandle string
            this.onunhandled(json);
          }
        });
      } else if (e.data instanceof ArrayBuffer) {
        warnLog(`(${this.url}) onmessage: unhandle ArrayBuffer`);
      } else if (e.data instanceof Blob) {
        warnLog(`(${this.url}) onmessage: unhandle Blob`);
      }
    };
  }

  private onunhandled(json: Record<string, any>) {
    try {
      if (json.type === "err") {
        this.onerror?.(json);
      } else if (json.event) {
        console.warn(
          "No handler found for event: %o. Message: %O",
          json.event,
          json
        );
      } else {
        console.warn("No handler found for message: ", json);
      }
    } catch (e) {
      // dev tools are disabled so we cannot use console on IE
    }
  }

  // 可能需要根据协议变更传输格式
  private _send(data: Record<string, any>) {
    if (this.socket?.readyState === WebSocketClient.OPEN) {
      // ["{\"type\":\"ping\"}"]
      // ["{\"type\":\"register\",\"address\":\"CHARGECONN_1\",\"headers\":{}}"]
      this.socket.send(JSON.stringify([JSON.stringify(data)]));
    } else {
      // 未发消息缓存
      this.messageQueue.push(data);
      this._connect();
    }
  }

  public ping() {
    this._send({ type: "ping" });
  }

  // 注册监听
  public register(
    address: string,
    listener: (err: any, message?: any) => void,
    headers: Record<string, string> = {}
  ) {
    if (!this.listeners[address]) {
      this.listeners[address] = [];
      // First handler for this address so we should register the connection
      this._send({
        type: "register",
        address: address,
        headers: mergeObjectDeep(
          headers,
          this.socketCreatorOptions.headers,
          false
        )
      });
    }
    this.listeners[address].push(listener);
  }

  // 取消注册监听
  public unregister(
    address: string,
    listener: (err: any, message?: any) => void,
    headers: Record<string, string> = {}
  ) {
    const listeners = this.listeners[address];
    if (listeners) {
      const idx = listeners.indexOf(listener);
      if (idx != -1) {
        listeners.splice(idx, 1);
        if (listeners.length === 0) {
          // No more local listeners so we should unregister the connection
          this._send({
            type: "unregister",
            address: address,
            headers: mergeObjectDeep(
              headers,
              this.socketCreatorOptions.headers,
              false
            )
          });
          delete this.listeners[address];
        }
      }
    }
  }

  // 广播
  public publish(
    address: string,
    body: Record<string, any>,
    headers: Record<string, string> = {}
  ) {
    this._send({
      type: "publish",
      address: address,
      headers: mergeObjectDeep(
        headers,
        this.socketCreatorOptions.headers,
        false
      ),
      body
    });
  }

  // 普通发送，等待回应
  public async send(
    address: string,
    body: Record<string, any>,
    headers: Record<string, string> = {},
    timeout = 30000
  ): Promise<string> {
    this.requestid++;
    const request: WebSocketClientRequest = (this.waitings[this.requestid] = {
      requestid: this.requestid
    });
    const receivedMessage = await new Promise<string>((resolve, reject) => {
      request.resolve = resolve;
      this._send({
        type: "send",
        address: address,
        headers: mergeObjectDeep(
          headers,
          this.socketCreatorOptions.headers,
          false
        ),
        body: {
          requestid: request.requestid,
          ...body
        }
      });
      setTimeout(() => {
        reject(
          `(${this.url}) send(${request.requestid}: ${toObjectString(
            body
          )}) Timeout!`
        );
      }, timeout);
    });
    delete this.waitings[request.requestid];
    return receivedMessage;
  }
}
